<script lang="ts">
  /**
   * Modal form for creating a new memory.
   *
   * Submits to POST /api/memory. New memories land with embedding=NULL
   * and are not visible in the constellation until the backfill daemon
   * computes their vector. The dialog displays this hint after submit.
   */

  import { writeMode, selection } from './selection.svelte'
  import { createMemory, WritesDisabledError, type CreateMemoryResult } from './api.svelte'

  let content = $state('')
  let topic = $state('')
  let importance = $state(5)
  let tagsRaw = $state('')   /* comma-separated input, parsed on submit */
  let memoryType = $state('insight')
  let agentId = $state('claude-nexus')

  let submitting = $state(false)
  let error = $state<string | null>(null)
  let result = $state<CreateMemoryResult | null>(null)

  function reset(): void {
    content = ''; topic = ''; importance = 5; tagsRaw = ''
    memoryType = 'insight'; agentId = 'claude-nexus'
    error = null; result = null; submitting = false
  }

  function close(): void {
    writeMode.addDialogOpen = false
    reset()
  }

  async function submit(): Promise<void> {
    if (submitting) return
    if (content.trim().length === 0) {
      error = 'content is required'
      return
    }
    submitting = true
    error = null
    try {
      const res = await createMemory({
        content,
        topic: topic.trim() || null,
        importance: Number(importance),
        tags: tagsRaw.split(',').map((t) => t.trim()).filter((t) => t.length > 0),
        memoryType: memoryType.trim() || null,
        agentId: agentId.trim() || null,
      })
      result = res
      /* Surface the new id in the side panel right away so the user sees it. */
      selection.selectedId = res.id
    } catch (err) {
      if (err instanceof WritesDisabledError) {
        error = 'Writes disabled — start mu-api with MU_API_WRITES_ENABLED=1'
      } else {
        error = (err as Error).message
      }
    } finally {
      submitting = false
    }
  }

  function onKey(e: KeyboardEvent): void {
    if (!writeMode.addDialogOpen) return
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
    } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      void submit()
    }
  }
</script>

<svelte:window onkeydown={onKey} />

{#if writeMode.addDialogOpen}
  <div class="add-backdrop" role="presentation" onclick={(e) => { if (e.target === e.currentTarget) close() }}>
    <div class="add-dialog" aria-label="Create memory">
      <header class="add-head">
        <span class="add-tag">// new memory</span>
        <button class="add-close" type="button" onclick={close} aria-label="Close">✕</button>
      </header>

      {#if result}
        <div class="add-body add-result">
          <div class="add-row-confirm">
            <span class="add-confirm-glyph">✓</span>
            <span class="add-confirm-text">CREATED #{result.id}</span>
          </div>
          <p class="add-hint">{result.embeddingHint}</p>
          <div class="add-actions">
            <button class="add-btn" type="button" onclick={() => { result = null; reset() }}>+ ANOTHER</button>
            <button class="add-btn add-btn-primary" type="button" onclick={close}>DONE</button>
          </div>
        </div>
      {:else}
        <form class="add-body" onsubmit={(e) => { e.preventDefault(); void submit() }}>
          <label class="add-field">
            <span class="add-k">topic</span>
            <input
              class="add-input"
              type="text"
              bind:value={topic}
              maxlength="200"
              placeholder="optional, brief"
              autocomplete="off"
              spellcheck="false"
            />
          </label>

          <label class="add-field">
            <span class="add-k">content</span>
            <textarea
              class="add-input add-textarea"
              bind:value={content}
              required
              maxlength="100000"
              placeholder="the memory body"
              rows="6"
            ></textarea>
          </label>

          <div class="add-row">
            <label class="add-field add-field-narrow">
              <span class="add-k">importance</span>
              <input
                class="add-input"
                type="number"
                bind:value={importance}
                min="0" max="25" step="1"
              />
            </label>

            <label class="add-field add-field-narrow">
              <span class="add-k">type</span>
              <select class="add-input" bind:value={memoryType}>
                <option value="insight">insight</option>
                <option value="fact">fact</option>
                <option value="event">event</option>
                <option value="decision">decision</option>
                <option value="reference">reference</option>
                <option value="test">test</option>
              </select>
            </label>

            <label class="add-field add-field-narrow">
              <span class="add-k">agent</span>
              <input class="add-input" type="text" bind:value={agentId} maxlength="100" />
            </label>
          </div>

          <label class="add-field">
            <span class="add-k">tags</span>
            <input
              class="add-input"
              type="text"
              bind:value={tagsRaw}
              placeholder="comma, separated"
              autocomplete="off"
              spellcheck="false"
            />
          </label>

          {#if error}
            <div class="add-error">{error}</div>
          {/if}

          <div class="add-actions">
            <span class="add-shortcut">⌘↵ to submit · ⎋ to close</span>
            <button class="add-btn" type="button" onclick={close}>CANCEL</button>
            <button class="add-btn add-btn-primary" type="submit" disabled={submitting}>
              {submitting ? 'SAVING…' : 'CREATE'}
            </button>
          </div>
        </form>
      {/if}
    </div>
  </div>
{/if}

<style>
  .add-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.78);
    backdrop-filter: blur(2px);
    z-index: 200;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 8vh var(--s-3) var(--s-3);
    animation: add-fade var(--t-mid);
  }
  @keyframes add-fade { from { opacity: 0 } to { opacity: 1 } }

  .add-dialog {
    width: 100%;
    max-width: 560px;
    max-height: 80vh;
    background: var(--surface);
    border: 1px solid var(--accent);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.7), 0 0 16px var(--accent-faint);
    display: flex;
    flex-direction: column;
    font-family: var(--font-mono);
  }
  .add-head {
    display: flex;
    align-items: center;
    gap: var(--s-2);
    padding: var(--s-2) var(--s-3);
    border-bottom: 1px solid var(--outline-variant);
    background: var(--surface-container-low);
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    flex-shrink: 0;
  }
  .add-tag { color: var(--accent); font-weight: 700; flex: 1; }
  .add-close {
    background: transparent;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-variant);
    width: 24px; height: 24px;
    cursor: pointer;
    line-height: 1;
  }
  .add-close:hover { color: var(--accent); border-color: var(--accent); }

  .add-body {
    display: flex;
    flex-direction: column;
    gap: var(--s-3);
    padding: var(--s-3);
    overflow-y: auto;
    flex: 1;
  }

  .add-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .add-field-narrow { flex: 1 1 100px; }
  .add-row {
    display: flex;
    gap: var(--s-3);
    flex-wrap: wrap;
  }
  .add-k {
    font-size: 10px;
    color: var(--on-surface-faint);
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
  .add-input {
    font-family: inherit;
    font-size: 12px;
    color: var(--on-surface);
    background: var(--surface-container-low);
    border: 1px solid var(--outline-variant);
    padding: 6px 8px;
    outline: none;
    transition: border-color var(--t-fast);
    width: 100%;
  }
  .add-input:focus { border-color: var(--accent); }
  .add-textarea {
    font-family: inherit;
    line-height: 1.5;
    resize: vertical;
    min-height: 100px;
  }

  .add-error {
    color: #ff6b6b;
    font-size: 11px;
    border: 1px dashed #ff6b6b;
    padding: 4px 8px;
    background: rgba(255, 107, 107, 0.05);
  }

  .add-actions {
    display: flex;
    align-items: center;
    gap: var(--s-2);
    margin-top: var(--s-2);
  }
  .add-shortcut {
    flex: 1;
    color: var(--on-surface-faint);
    font-size: 9px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .add-btn {
    font: inherit;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    padding: 6px 12px;
    background: transparent;
    color: var(--on-surface-variant);
    border: 1px solid var(--outline-variant);
    cursor: pointer;
    transition: color var(--t-fast), border-color var(--t-fast), background var(--t-fast);
  }
  .add-btn:hover { color: var(--accent); border-color: var(--accent); }
  .add-btn-primary {
    color: var(--accent);
    border-color: var(--accent);
  }
  .add-btn-primary:hover {
    background: var(--accent-faint);
    color: var(--accent-bright);
  }
  .add-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Result confirm screen */
  .add-result {
    align-items: center;
    text-align: center;
    padding: var(--s-6) var(--s-3);
  }
  .add-row-confirm {
    display: flex;
    align-items: center;
    gap: var(--s-2);
  }
  .add-confirm-glyph {
    color: var(--accent);
    font-size: 24px;
    line-height: 1;
  }
  .add-confirm-text {
    color: var(--accent-bright);
    font-size: 14px;
    letter-spacing: 0.16em;
    font-weight: 700;
  }
  .add-hint {
    color: var(--on-surface-variant);
    font-size: 11px;
    line-height: 1.5;
    max-width: 380px;
    margin: 0;
  }
</style>
